import type { DatasetData, GroundTruthLabel, OverlaySelection } from './types';

const KEYPOINT_COLORS = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
const KEYPOINT_RADIUS = 6;

/** Palette for class-colored shaded bounding boxes. */
const CLASS_COLORS = [
  '#3498db',
  '#2ecc71',
  '#e74c3c',
  '#f39c12',
  '#9b59b6',
  '#1abc9c',
  '#e67e22',
  '#2980b9',
];

const FILL_ALPHA = 0.3;

export type DrawBboxOptions = {
  showLabels?: boolean;
  visibleClasses?: Set<string> | null;
  clear?: boolean;
};

function hashClassName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Stable color for a bbox class name. */
export function colorForClassName(className: string): string {
  return CLASS_COLORS[hashClassName(className) % CLASS_COLORS.length];
}

function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace('#', '');
  const n =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function formatWeightLbs(weight: number): string {
  return `${weight.toFixed(2)} lbs`;
}

export function parseWeightFromMetadata(metadata: DatasetData['metadata']): number | null {
  const raw = metadata?.weight;
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return Number.isFinite(n) ? n : null;
}

export function drawWeightLabel(
  canvas: HTMLCanvasElement,
  data: DatasetData,
  clear = true
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  if (clear) ctx.clearRect(0, 0, canvas.width, canvas.height);
  const weight = parseWeightFromMetadata(data.metadata);
  if (weight == null) return;

  const labelText = `Weight: ${formatWeightLbs(weight)}`;
  const displayWidth = parseFloat(canvas.dataset.displayWidth || String(canvas.width));
  const displayHeight = parseFloat(canvas.dataset.displayHeight || String(canvas.height));

  ctx.font = 'bold 16px Arial';
  const labelWidth = ctx.measureText(labelText).width + 16;
  const labelHeight = 28;
  const x = (displayWidth - labelWidth) / 2;
  const y = displayHeight - labelHeight - 10;

  ctx.fillStyle = 'rgba(230, 126, 34, 0.92)';
  ctx.fillRect(x, y, labelWidth, labelHeight);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(labelText, x + 8, y + 19);
}

function resolveBoxRect(
  box: GroundTruthLabel,
  naturalWidth: number,
  naturalHeight: number,
  displayWidth: number,
  displayHeight: number
): { x: number; y: number; w: number; h: number } | null {
  if (
    !box.bbox ||
    typeof box.bbox.x1 !== 'number' ||
    typeof box.bbox.y1 !== 'number' ||
    typeof box.bbox.x2 !== 'number' ||
    typeof box.bbox.y2 !== 'number'
  ) {
    return null;
  }

  const isNormalized =
    box.bbox.x1 <= 1 &&
    box.bbox.x2 <= 1 &&
    box.bbox.y1 <= 1 &&
    box.bbox.y2 <= 1 &&
    box.bbox.x1 >= 0 &&
    box.bbox.y1 >= 0;

  const scaleX = naturalWidth > 0 ? displayWidth / naturalWidth : 1;
  const scaleY = naturalHeight > 0 ? displayHeight / naturalHeight : 1;

  if (isNormalized) {
    return {
      x: box.bbox.x1 * displayWidth,
      y: box.bbox.y1 * displayHeight,
      w: (box.bbox.x2 - box.bbox.x1) * displayWidth,
      h: (box.bbox.y2 - box.bbox.y1) * displayHeight,
    };
  }
  return {
    x: box.bbox.x1 * scaleX,
    y: box.bbox.y1 * scaleY,
    w: (box.bbox.x2 - box.bbox.x1) * scaleX,
    h: (box.bbox.y2 - box.bbox.y1) * scaleY,
  };
}

/**
 * Draw shaded (fill + stroke) bounding boxes from a flat label list.
 * Used by detail overlay and thumbnail grid.
 */
export function drawBboxLabelsOnCanvas(
  canvas: HTMLCanvasElement,
  labels: GroundTruthLabel[],
  options?: DrawBboxOptions
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  if (options?.clear !== false) ctx.clearRect(0, 0, canvas.width, canvas.height);

  const showLabels = options?.showLabels !== false;
  const visibleClasses = options?.visibleClasses;
  const labelsWithBbox = labels.filter((l) => l.bbox);
  if (labelsWithBbox.length === 0) return;

  const naturalWidth = parseFloat(canvas.dataset.imageWidth || '0');
  const naturalHeight = parseFloat(canvas.dataset.imageHeight || '0');
  const displayWidth = parseFloat(canvas.dataset.displayWidth || String(canvas.width));
  const displayHeight = parseFloat(canvas.dataset.displayHeight || String(canvas.height));

  labelsWithBbox.forEach((box, index) => {
    try {
      const className = box.class_name || `Class ${box.class_id ?? index}`;
      if (visibleClasses && !visibleClasses.has(className)) return;

      const rect = resolveBoxRect(box, naturalWidth, naturalHeight, displayWidth, displayHeight);
      if (!rect) {
        console.warn('Unknown bounding box format:', box);
        return;
      }

      const { x, y, w, h } = rect;
      const color = colorForClassName(className);

      ctx.fillStyle = hexToRgba(color, FILL_ALPHA);
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, w, h);

      if (!showLabels) return;

      const conf = box.confidence ? ` (${(box.confidence * 100).toFixed(1)}%)` : '';
      const labelText = className + conf;
      ctx.font = '12px Arial';
      const labelWidth = ctx.measureText(labelText).width + 8;
      const labelHeight = 16;

      ctx.fillStyle = color;
      ctx.fillRect(x, y - labelHeight, labelWidth, labelHeight);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(labelText, x + 4, y - 4);
    } catch (error) {
      console.error('Error processing bounding box:', error);
    }
  });
}

export function drawBoundingBoxes(
  canvas: HTMLCanvasElement,
  data: DatasetData,
  selection: OverlaySelection,
  options?: DrawBboxOptions
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (selection.bboxes) {
    const labelsWithBbox = data.ground_truth_labels?.filter((l) => l.bbox) ?? [];
    drawBboxLabelsOnCanvas(canvas, labelsWithBbox, {
      showLabels: options?.showLabels !== false,
      visibleClasses: options?.visibleClasses,
      clear: false,
    });
  }
  if (selection.weights) {
    drawWeightLabel(canvas, data, false);
  }
  if (selection.keypoints) {
    drawKeypoints(canvas, data);
  }
}

export function drawKeypoints(canvas: HTMLCanvasElement, data: DatasetData): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || !data.keypoints?.length) return;
  const naturalWidth = parseFloat(canvas.dataset.imageWidth || '0');
  const naturalHeight = parseFloat(canvas.dataset.imageHeight || '0');
  const displayWidth = parseFloat(canvas.dataset.displayWidth || '0');
  const displayHeight = parseFloat(canvas.dataset.displayHeight || '0');
  const scaleX = naturalWidth > 0 ? displayWidth / naturalWidth : 1;
  const scaleY = naturalHeight > 0 ? displayHeight / naturalHeight : 1;
  data.keypoints.forEach((kp, i) => {
    const dx = kp.x * scaleX;
    const dy = kp.y * scaleY;
    const color = KEYPOINT_COLORS[i % KEYPOINT_COLORS.length];
    ctx.beginPath();
    ctx.arc(dx, dy, KEYPOINT_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.strokeStyle = '#ff0000';
    ctx.lineWidth = 2;
    ctx.stroke();
    const label = `${kp.category} (${kp.x}, ${kp.y})`;
    ctx.font = '12px Arial';
    const textWidth = ctx.measureText(label).width + 8;
    const textHeight = 16;
    const textX = dx + KEYPOINT_RADIUS + 4;
    const textY = dy - KEYPOINT_RADIUS - 4;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(textX - 2, textY - textHeight + 2, textWidth, textHeight);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, textX + 2, textY);
  });
}

export function setupBoundingBoxOverlay(
  img: HTMLImageElement,
  canvas: HTMLCanvasElement,
  data: DatasetData,
  selection: OverlaySelection,
  options?: DrawBboxOptions
): void {
  if (!img.complete) {
    img.onload = () => setupBoundingBoxOverlay(img, canvas, data, selection, options);
    return;
  }

  canvas.width = img.offsetWidth;
  canvas.height = img.offsetHeight;
  canvas.dataset.imageWidth = img.naturalWidth.toString();
  canvas.dataset.imageHeight = img.naturalHeight.toString();
  canvas.dataset.displayWidth = img.offsetWidth.toString();
  canvas.dataset.displayHeight = img.offsetHeight.toString();
  drawBoundingBoxes(canvas, data, selection, options);
}

/** Size canvas to an image element and draw bbox labels (for thumbnails). */
export function setupThumbnailBboxOverlay(
  img: HTMLImageElement,
  canvas: HTMLCanvasElement,
  labels: GroundTruthLabel[]
): void {
  const draw = () => {
    canvas.width = img.offsetWidth;
    canvas.height = img.offsetHeight;
    canvas.dataset.imageWidth = img.naturalWidth.toString();
    canvas.dataset.imageHeight = img.naturalHeight.toString();
    canvas.dataset.displayWidth = img.offsetWidth.toString();
    canvas.dataset.displayHeight = img.offsetHeight.toString();
    drawBboxLabelsOnCanvas(canvas, labels, { showLabels: false });
  };

  if (!img.complete || img.naturalWidth === 0) {
    img.onload = draw;
    return;
  }
  draw();
}
